import React, { useEffect, useMemo, useRef, useState } from "react"
import { capitalize } from "lodash"
import {
	AntdButton,
	AntdInput,
	AntdSelect1,
	DateField,
	FormCheckbox,
	FormInputAntd,
	FormSkeleton,
	FormSwitchInput,
	GlobalSidebar,
	PasswordChangeModal,
	SidebarList,
	UserAccessModal,
} from "components"
import { FormProvider, SubmitHandler, useForm } from "react-hook-form"
import { yupResolver } from "@hookform/resolvers/yup"
import { userInitialValues, usersSchema } from "Schema/Security/security"
import { UserInterface } from "types/security.types"
import useAppDispatch from "hooks/useAppDispatch"
import useAppSelector from "hooks/useAppSelector"
import {
	getUsers,
	isUserLoading,
	isUserSaving,
	isUserPasswordSaving,
	saveUser,
	selectUserById,
	selectUserList,
} from "store/Security/users.slice"
import { platformTypeENUM } from "config/constants"
import { selectCurrentUser } from "store/auth.slice"
import { CaretDownOutlined, SearchOutlined } from "@ant-design/icons"
import { Divider } from "antd-v5"
import { sortByProperty } from "config/utils"
import { useAdminQueryParams } from "hooks"

interface UsersFormHandler extends UserInterface {}

const Users = () => {
	const [passwordModalHandler, setPasswordModalHandler] = useState(false)
	// const [userRoles, setUserRoles] = useState<GetRoleInterface[]>([])
	const dispatch = useAppDispatch()
	const loading = useAppSelector(isUserLoading())
	const saving = useAppSelector(isUserSaving())
	const passwordSaving = useAppSelector(isUserPasswordSaving())
	const userList = useAppSelector(selectUserList)
	const { params, setSearchParams } = useAdminQueryParams()
	const { selected: selectedUser = "", platform: selectedPlatform = platformTypeENUM.MOBILE } = params
	const user = useAppSelector(selectUserById(selectedUser))
	const isNewBeingCreated = useRef(false)
	const currentUser = useAppSelector(selectCurrentUser())
	const [searchString, setSearchString] = useState<string>("")

	const [userAccessModal, setUserAccessModal] = useState(false)
	const methods = useForm<UsersFormHandler>({ resolver: yupResolver(usersSchema), mode: "all" })

	const {
		handleSubmit,
		reset,
		setValue,
		watch,
		formState: { errors, isSubmitting },
	} = methods
	const platformType = Object.keys(platformTypeENUM)
		.map(item => ({ label: capitalize(item), key: item }))
		.sort(sortByProperty("label"))

	const sidebarUsers = useMemo(() => {
		return (
			userList
				?.filter(
					user =>
						user.firstName.toLowerCase().includes(searchString.toLowerCase()) ||
						user.lastName.toLowerCase().includes(searchString.toLowerCase()),
				)
				.map(user => ({
					_id: user._id,
					name: `${user.firstName} ${user.lastName}`,
				})) || []
		)
	}, [userList, searchString])

	const onSubmit: SubmitHandler<UsersFormHandler> = data => {
		if (data.password === "#" && data.confirmPassword === "#") {
			delete data.confirmPassword
			delete data.password
		}
		if (data.lastLogin) {
			delete data.lastLogin
		}
		isNewBeingCreated.current = true

		const payload = { ...data, platform: selectedPlatform, expiryDate: data?.expiryDate ? data?.expiryDate : null }

		dispatch(
			saveUser(payload, id => {
				setSearchParams({ selected: id })
				setTimeout(() => (isNewBeingCreated.current = false), 200)
			}),
		)
	}

	useEffect(() => {
		dispatch(getUsers(selectedPlatform))
	}, [selectedPlatform])

	useEffect(() => {
		if (!selectedUser && !!userList?.length && !isNewBeingCreated.current) {
			setSearchParams({ selected: userList[0]._id })
		}
	}, [params, userList])

	useEffect(() => {
		if (selectedUser && user) {
			reset({
				...userInitialValues,
				...user,
				password: "#",
				confirmPassword: "#",
			})
		} else if (!selectedUser && !isNewBeingCreated.current) {
			reset({ ...userInitialValues })
		}
	}, [selectedUser, user])

	// Handle user selection logic
	useEffect(() => {
		if (sidebarUsers?.length && !isNewBeingCreated.current) {
			// If no user is selected, select the first user
			if (!selectedUser) {
				setSearchParams({ selected: sidebarUsers[0]._id, platform: selectedPlatform })
			}
			// If selected user doesn't exist in the current user list, select the first user
			else if (!sidebarUsers.some(user => user._id === selectedUser)) {
				setSearchParams({ selected: sidebarUsers[0]._id, platform: selectedPlatform })
			}
		}
	}, [sidebarUsers, selectedUser, params])

	// Handle search filtering selection
	// useEffect(() => {
	// 	if (searchString.length && sidebarUsers.length) {
	// 		// If the currently selected user is not in the filtered results, select the first filtered user
	// 		const isSelectedUserInFilteredResults = sidebarUsers.some(user => user._id === selectedUser)
	// 		if (!isSelectedUserInFilteredResults) {
	// 			setSearchParams({ selected: sidebarUsers[0]._id })
	// 		}
	// 	}
	// }, [searchString, sidebarUsers, selectedUser, params])

	const handleSelected = (userId: string) => {
		setSearchParams({ selected: userId })
		isNewBeingCreated.current = false
	}

	return (
		<>
			<GlobalSidebar>
				<div className="flex flex-col space-y-2">
					<AntdSelect1
						onChange={value => setSearchParams({ platform: value })}
						value={selectedPlatform}
						placeholder="Select Platform"
						options={platformType.map(item => ({
							label: item.label,
							value: item.key,
						}))}
						suffixIcon={
							<>
								<span style={{ color: "rgb(156 163 175)" }}>Platform</span>
								<CaretDownOutlined style={{ color: "rgb(156 163 175)" }} />
							</>
						}
					/>

					<AntdInput
						onChange={e => setSearchString(e.target.value)}
						value={searchString}
						placeholder="Search"
						type="text"
						suffix={<SearchOutlined size={14} style={{ color: "rgb(156 163 175)" }} />}
					/>
				</div>
				<Divider className="bg-slate-700" style={{ margin: "15px 0 10px 0" }} />
				<SidebarList
					title={"Users"}
					loading={loading}
					selected={selectedUser}
					setSelected={handleSelected}
					list={sidebarUsers.sort(sortByProperty("name"))}
				/>
			</GlobalSidebar>
			{loading ? (
				<FormSkeleton />
			) : (
				<>
					<FormProvider {...methods}>
						<form className="flex-col py-2" onSubmit={handleSubmit(onSubmit)}>
							<div className="grid grid-cols-2 gap-5">
								<div className="space-y-5">
									<div className="grid grid-cols-2 gap-x-5 gap-y-3">
										<FormInputAntd type="text" label="First Name" name="firstName" />
										<FormInputAntd type="text" label="Last Name" name="lastName" />
										<FormInputAntd type="text" label="User Name" name="userName" />
										<FormInputAntd type="text" label="Email" name="email" />

										{!selectedUser ? (
											<div className="col-span-2">
												<div className="flex flex-col space-y-2">
													<div className="grid grid-cols-2 gap-x-5">
														<FormInputAntd
															autoComplete="new-password"
															type="password"
															label="Password"
															name="password"
															hidden={!!selectedUser}
														/>

														<FormInputAntd
															type="password"
															autoComplete="new-password"
															label="Confirm Password"
															name="confirmPassword"
															hidden={!!selectedUser}
														/>
													</div>
												</div>

												<FormCheckbox
													containerClassName="h-10 flex items-center "
													label="User must change password at next logon"
													name="shouldChangePassword"
												/>
											</div>
										) : null}
										<FormInputAntd type="text" label="Mobile" name="mobile" />
										<FormInputAntd type="text" label="Telephone 1" name="telephone1" />
										<div className="col-span-2 grid grid-cols-2 gap-x-5">
											<FormInputAntd
												className="col-span-1"
												type="text"
												label="Telephone 2"
												name="telephone2"
											/>
										</div>

										<DateField
											label="Expiry Date"
											name={"expiryDate"}
											setValue={setValue}
											watch={watch}
											errors={errors}
										/>
										<FormInputAntd
											type="number"
											min={0}
											label="Inactivity Timer (minutes)(Default: 10)"
											name="inactivityTimer"
										/>
										<FormSwitchInput
											label="Enabled"
											name="isEnabled"
											watch={watch}
											errors={errors}
											setValue={setValue}
										/>

										{/* <p className="mt-5">
										<span className="font-bold">NOTICE :</span> Users/ branches and employees
										created beyond the limits defined in your license may incur additional charges
									</p> */}
									</div>
									<div className="flex gap-x-2">
										<AntdButton
											onClick={() => {
												isNewBeingCreated.current = true
												reset({ ...userInitialValues })
												setSearchParams({ ...params, selected: "" })
											}}
										>
											New
										</AntdButton>
										<AntdButton disabled={isSubmitting} loading={saving} htmlType="submit">
											{selectedUser ? "Update" : "Save"}
										</AntdButton>
									</div>
								</div>

								<div className="flex w-full flex-col space-y-2">
									<FormInputAntd
										containerClass="max-w-2xl w-full"
										className=" mr-5.5 mt-1.25 w-auto"
										label="Notes"
										name="notes"
										type="textarea"
									/>
									<div className="flex space-x-4">
										{selectedPlatform === platformTypeENUM.WEB &&
											currentUser?.roles.some(
												role => role.hasCompanyWideAccess || role.isOrganizationalAdmin,
											) && (
												<AntdButton
													onClick={() => setUserAccessModal(true)}
													className="mt-5 w-fit"
													disabled={!selectedUser}
												>
													User Access
												</AntdButton>
											)}
										<AntdButton
											onClick={() => setPasswordModalHandler(!passwordModalHandler)}
											loading={passwordSaving}
											disabled={passwordSaving || !selectedUser}
											className="mt-5 w-fit"
										>
											Change Password
										</AntdButton>
									</div>
								</div>
							</div>
						</form>
					</FormProvider>
					<PasswordChangeModal
						open={passwordModalHandler}
						modalHandler={setPasswordModalHandler}
						userId={selectedUser}
					/>
					{userAccessModal && selectedUser && selectedPlatform === platformTypeENUM.WEB && (
						<UserAccessModal
							open={userAccessModal}
							modalHandler={setUserAccessModal}
							selectedUser={selectedUser}
						/>
					)}
				</>
			)}
		</>
	)
}

export default Users
